import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { Injector } from '@angular/core';
import { PanelBarTemplate } from '../../../../../shared/expandable-panelbar/expandable-panelbar.component';
import { PanelbarService } from '../../../../../shared/expandable-panelbar/expandable-panelbar.service';
import { CtwrRequestService } from '../../../ctwr.service';
import { RequestDetailService } from '../detail.service';
import { ApprovalStatus } from '../../../workflow.service';
import { UserDelegateTemplate } from '../delegate.template';
import { AssignPLFormDialogTemplate } from './assign-pl-dialog.template';
import { CgpConfirmationDialogService } from '../../../../../shared/cgp-confirmation-dialog/cgp-confirmation-dialog.service';
import { UploadComponent, FileInfo, FileState } from '@progress/kendo-angular-upload';
import { S3Service } from '../../../../../shared/services/s3.service';
import { FormDialogTemplate } from '../../../../../shared/form-dialog/form-dialog.template';
import { BehaviorSubject } from 'rxjs';
import { DatePipe } from '@angular/common';
import { HttpParams } from '@angular/common/http';


export class ToolingComponentPanelbar extends PanelBarTemplate {

    private readonly formBuilder: FormBuilder;
    private readonly assignPLDialogTemplate: AssignPLFormDialogTemplate;
    private readonly commentDialog: FormDialogTemplate = new FormDialogTemplate();
    private readonly commentDialogTitle: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private readonly userDelegateTemplate: UserDelegateTemplate;
    private readonly confirmationDialog: CgpConfirmationDialogService;
    private readonly panelbarService: PanelbarService;
    private readonly requestService: CtwrRequestService;
    private readonly detailService: RequestDetailService;
    private readonly s3Service: S3Service;
    private requestDetail: any;

    constructor(
        private readonly injector: Injector,
    ) {
        super();

        this.formBuilder = injector.get(FormBuilder);
        this.confirmationDialog = injector.get(CgpConfirmationDialogService);
        this.panelbarService = injector.get(PanelbarService);
        this.requestService = injector.get(CtwrRequestService);
        this.detailService = injector.get(RequestDetailService);
        this.s3Service = injector.get(S3Service);

        this.assignPLDialogTemplate = new AssignPLFormDialogTemplate(this.injector);
        this.userDelegateTemplate = new UserDelegateTemplate(this.injector);

        this.detailService.requestDetail
            .subscribe(x => this.requestDetail = x);

        this.initCommentDialog();
    }


    /**
     * For each Part Approval in the dataItem, create an editForm
     * @param dataItem PartApprovalMatrix Dto
     */
    setComponentData(dataItem: any) {
        this.reset();
        this.setComponentForm(dataItem);
        dataItem.ToolingItems.forEach(i => {
            i.ParentEditDataItem = dataItem;
            const _form: FormGroup = this.getEditForm(i);
            this.detailService.addTask(i.PTEWFTaskId, _form, this);
            this.detailService.addTask(i.DESWFTaskId, _form, this);
            this.forms.push(_form);
        });
        const _attachments = this.mapAttachmentDto(dataItem.Attachments);
        const _uploadForm = this.formBuilder.group(
            {
                ComponentId: dataItem.ComponentId,
                EnableAttachments: dataItem.EnableAttachments,
                Attachments: [_attachments],
                ParentEditDataItem: dataItem.ComponentCategory
            });
        if (dataItem.EnableAttachments || _attachments.length) {
            this.forms.push(_uploadForm);
        }
    }


    /**
     * 
     * @param dataItem 
     */
    setComponentForm(dataItem: any) {
        const _form = this.formBuilder.group(ComponentEditForm);
        dataItem.ParentEditDataItem = dataItem.ComponentCategory;
        _form.reset(dataItem);

        dataItem.EnableDesignerAssignment ?
            _form.get('AssignPLBtn').enable() :
            _form.get('AssignPLBtn').disable();
        this.forms.push(_form);
    }


    /**
     * 
     */
    refresh = () => {
        this.loading.next(true);
        this.detailService.refreshWorkApprovalMatrix()
    }


    /**
     * 
     */
    initCommentDialog() {
        const form = this.formBuilder.group({
            Response: ['', Validators.compose([Validators.required, Validators.maxLength(255)])]
        });
        const controls = [{
            selected: true,
            title: '',
            formControls: [{
                type: 'text',
                name: 'Response',
                placeholder: 'Enter a value',
                title: 'Enter a value',
                stretch: true,
            },
            {
                type: 'radiogroup',
                title: 'Did you change a library or common part?',
                name: 'isLibraryPart',
                inline: true,
                options: [
                    {
                        value: true,
                        label: 'Yes',
                    },
                    {
                        value: false,
                        label: 'No',
                    },
                ]
            },
            ]
        }]
        this.commentDialog.initilize(this.commentDialogTitle, form, controls);
    }


    /**
     * 
     * @param files 
     */
    private mapAttachmentDto(files: any[]): FileInfo[] {
        return files ? files
            .map(i => {
                return {
                    name: i.FileName,
                    CreatedBy: i.CreatedBy,
                    CreatedDate: i.CreatedDate,
                    uid: i.Id,
                    state: FileState.Uploaded,
                    comment: i.FileDescription
                } as FileInfo;
            }) : null;
    }


    /**
     * Use parameters to construct and return a FormGroup
     * @param dataItem - form data
     * @param parentEditDataItem - workflow task data
     */
    private getEditForm(dataItem: any): FormGroup {
        const form = this.formBuilder.group(ItemEditForm);
        dataItem.DESCompletionDate = new DatePipe('en-US')
            .transform(dataItem.DESCompletionDate, 'short');
        dataItem.PTECompletionDate = new DatePipe('en-US')
            .transform(dataItem.PTECompletionDate, 'short');
        form.reset(dataItem);
        this.updateFormValidation(form);
        return form;
    }


    /**
     * 
     * @param editForm 
     */
    private updateFormValidation(editForm: FormGroup) {
        const IsDESEnabled: boolean = editForm.get('IsDESEnabled').value,
            isPTEEnabled: boolean = editForm.get('IsPTEEnabled').value,
            isDeleteEnabled: boolean = editForm.get('IsDeleteEnabled').value,
            isCancelEnabled: boolean = editForm.get('IsCancelEnabled').value;

        // Add | Remove
        editForm.removeControl('desCancelBtn');
        editForm.removeControl('desCompleteBtn');
        editForm.removeControl('desDeleteBtn');
        editForm.removeControl('pteApproveBtn');

        if (IsDESEnabled || isPTEEnabled) {
            editForm.addControl('desCompleteBtn', new FormControl())
            editForm.addControl('desCancelBtn', new FormControl())
            if (!isCancelEnabled) editForm.get('desCancelBtn').disable();
        } else if (isDeleteEnabled) {
            editForm.addControl('desDeleteBtn', new FormControl())
        }

        editForm.get('pteReworkBtn').disable();

        // Enable | Disable
        if (IsDESEnabled) {
            editForm.get('desCompleteBtn').enable();
        } else if (isPTEEnabled) {
            editForm.get('desCompleteBtn').disable();
            editForm.get('pteReworkBtn').enable();
            editForm.addControl('pteApproveBtn', new FormControl())
        }
    }

    /**
     * 
     */
    private readonly handleUserDelegateSave = (dataItem, form, isDes) => {
        this.loading.next(true);
        form.get('IsDESEnabled').setValue(false);
        form.get('IsPTEEnabled').setValue(false);
        this.updateFormValidation(form);

        this.requestService.save({}, false, `/WFTask/Delegate?id=${dataItem.WFTaskId}&assignedTo=${dataItem.Email}`, false)
            .then(() => {
                this.loading.next(false);
                this.refresh();
            })
            .catch(e => {
                this.requestService.handleError(e);
                this.loading.next(false);
            });



    }


    /**
     * 
     */
    private readonly handleDelegateUser = (form: any = null, control: any = null) => {
        if (!form && !control) return this.detailService.isCtwrAdmin;
        this.panelbarService.openDialog(this.userDelegateTemplate);
        const isDes: boolean = control.name === 'AssignedDESName';
        this.userDelegateTemplate.refreshUsers(isDes ? 'DES' : 'PTE');
        const wfTaskId = isDes ? form.value.DESWFTaskId : form.value.PTEWFTaskId;
        this.userDelegateTemplate.next({ Email: form.value.PTEEmail, WFTaskId: wfTaskId });
        this.userDelegateTemplate.save = ({ dataItem }) => {
            this.handleUserDelegateSave(dataItem, form, isDes);
        }
        return null;
    }


    /**
     * 
     */
    private readonly handleAssignDesignerSave = (dataItem, form) => {
        const parent = form.value.ParentEditDataItem;
        const params: HttpParams = new HttpParams()
            .set('componentCategoryId', parent.ComponentCategoryId)
            .set('componentId', form.value.ComponentId)
            .set('partId', parent.PartId)
            .set('DesignerUserId', dataItem.Id)
            .set('requestId', parent.RequestId);
        const _path = `/AssignDesignerToRequestComponent?${params}`;
        this.loading.next(true);
        this.detailService.wfTask = {
            ComponentCategoryId: parent.ComponentCategoryId,
            ComponentId: form.value.ComponentId,
            RequestId: parent.RequestId
        }
        this.requestService.save({}, true, _path)
            .then(x => this.refresh());
    }


    /**
     * 
     */
    private readonly handleAssignDesigner = (form: any = null, control: any = null) => {
        this.assignPLDialogTemplate.refreshUsers('DES');
        this.panelbarService.openDialog(this.assignPLDialogTemplate);
        this.assignPLDialogTemplate.next({ Email: form.value.PTEEmail });
        this.assignPLDialogTemplate.save = ({ dataItem }) => this.handleAssignDesignerSave(dataItem, form);
    }


    /**
     * 
     */
    private readonly handleFormAction = (form: FormGroup, control, status: ApprovalStatus = null, response: string = undefined) => {
        const dataItem = form.value,
            _wfId = control.name.startsWith('des') ? dataItem.DESWFTaskId : dataItem.PTEWFTaskId,
            _dto = {
                Status: status
            }

        if (response) _dto['Response'] = response;


        this.loading.next(true);
        form.get('IsDESEnabled').setValue(false);
        form.get('IsPTEEnabled').setValue(false);
        this.updateFormValidation(form);

        this.requestService.save(_dto, false, `/WFTask/UpdateTaskStatus?id=${_wfId}`, false)
            .then(() => {
                this.loading.next(false);
                this.refresh();
            })
            .catch(e => {
                this.requestService.handleError(e);
                this.loading.next(false);
            });


    }


    /**
     * 
     */
    private readonly handleDelete = (form: FormGroup, control: any) => {
        const dataItem = form.value;
        this.confirmationDialog.showConfirmationDialog({
            title: 'Delete Confirmation',
            message: `Are you sure you want to delete ${dataItem.ItemName}?`,
            alert: 'danger',
            onYesClick: () => {
                this.confirmationDialog.closeConfirmationDialog();

                const _requestId = this.requestDetail.RequestId,
                    _requestItemId = form.value.RequestItemId;

                this.loading.next(true);
                this.requestService.remove(`/DeleteRequestItem?requestItemId=${_requestItemId}&requestId=${_requestId}`)
                    .then(() => {
                        this.loading.next(false);
                        this.refresh();
                    })
                    .catch(e => {
                        this.requestService.handleError(e);
                        this.loading.next(false);
                    });
            },
            onNoClick: () => {
                this.confirmationDialog.closeConfirmationDialog();
            }
        });

    }


    /**
     * 
     */
    private readonly handleCancel = (form: FormGroup, control: any) => {
        const _requestId = this.requestDetail.RequestId,
            _requestItemId = form.value.RequestItemId;

        this.commentDialog.save = ({ dataItem }) => {
            this.loading.next(true);
            form.get('IsDESEnabled').setValue(false);
            form.get('IsPTEEnabled').setValue(false);
            this.updateFormValidation(form);

            this.detailService.wfTask = {
                RequestItemId: _requestItemId
            }
            this.requestService.save(dataItem.Response, false, `/CancelRequestItem?requestId=${_requestId}&requestItemId=${_requestItemId}`)
                .then(() => {
                    this.loading.next(false);
                    this.refresh();
                })
                .catch(e => {
                    this.requestService.handleError(e);
                    this.loading.next(false);
                });

        }
        this.commentDialogTitle.next('Cancel Request: Reason')
        this.commentDialog.next({});
        this.panelbarService.openDialog(this.commentDialog);
    }


    /**
     * 
     */
    private readonly handleComplete = (form: FormGroup, control: any) => {
        this.commentDialog.save = ({ dataItem }) => {
            const _libraryPart = dataItem.isLibraryPart !== undefined && dataItem.isLibraryPart !== null && dataItem.isLibraryPart !== false;
            const response = {
                ECONumber: dataItem.Response,
                IsLibraryPartChange: _libraryPart
            }
            this.handleFormAction(form, control, ApprovalStatus.COMPLETE, JSON.stringify(response));
        }
        this.commentDialogTitle.next('Complete: Enter an ECO Number')
        this.commentDialog.editForm.addControl('isLibraryPart', new FormControl(null, Validators.required))
        this.commentDialog.next({});
        this.panelbarService.openDialog(this.commentDialog);
    }


    /**
     * 
     */
    private readonly handleApprove = (form: FormGroup, control: any) => {
        this.handleFormAction(form, control, ApprovalStatus.APPROVED)
    }


    /**
     * 
     */
    private readonly handleReject = (form: FormGroup, control: any) => {
        this.commentDialog.save = ({ dataItem }) => {
            this.handleFormAction(form, control, ApprovalStatus.REWORK_REQUESTED, dataItem.Response)
        }
        this.commentDialogTitle.next('Rework Request: Reason')
        this.commentDialog.next({});
        this.panelbarService.openDialog(this.commentDialog);
    }


    /**
     * Build array of path values; return joined values separated by '/'
     * CTWR/{RequestNumber}/{PartNumber}/{ComponentCategoryName}/{ComponentName}
     * @param dataItem
     */
    private getObjectKey(dataItem: any): string {
        return [
            'CTWR',
            this.requestDetail.RequestNumber,
            this.cleanUpName(dataItem.ParentEditDataItem.PartNumber),
            this.cleanUpName(dataItem.ParentEditDataItem.CategoryName),
            this.title.split(' - ')[0],
        ].join('/');
    }


    /**
     * 
     * @param name 
     */
    private cleanUpName(name: string): string {
        return name.replace(/[\W_]+/g, "_").replace('_amp', '');
    }


    /**
     * 
     */
    private readonly handleAttachmentUpload = (files: any[], form: FormGroup, c: UploadComponent) => {
        const _path: string = this.getObjectKey(form.value) + '/';
        const _parent: any = form.value.ParentEditDataItem;
        const params: HttpParams = new HttpParams()
            .set('requestId', _parent.RequestId)
            .set('partId', _parent.PartId)
            .set('componentCategoryId', _parent.ComponentCategoryId)
            .set('componentId', form.value.ComponentId)
        const _approvalUploadPath = `/UploadWorkApprovalItemAttachment?${params.toString()}`

        files = files.map(f => {
            const name: string = f.name;
            const fileParts = name.split('.');
            const type = `.${fileParts.pop()}`;
            f.name = fileParts.join('.').replace(/[\W_]+/g, "_").replace('_amp', '') + type;
            return f;
        });

        this.s3Service.handleS3Upload(files, _path)
            .then(x => {
                this.handleRequestAttachment(files, _approvalUploadPath, c, form);
            })
            .catch(e => {
                if (e.status === 200) {
                    this.handleRequestAttachment(files, _approvalUploadPath, c, form);
                }
            })
    }

    private handleRequestAttachment(files: any[], path, c: any, form) {
        files = files.map(f => {
            return {
                FileName: f.name,
                FileDescription: f.comment,
                Path: f.name,
                uid: f.uid
            }
        });

        this.requestService.save(files, true, path)
            .then(x => {
                c.fileList.remove(files[0].uid);
                files[0].uid = x;
                files[0].CreatedBy = this.detailService.loggedInUser.cn;
                files[0].CreatedDate = new Date().toLocaleDateString();
                files[0].state = FileState.Uploaded;
                files[0].name = files[0].FileName;
                files[0].comment = files[0].FileDescription;
                c.fileList.add(files[0]);
                form.get('Attachments').setValue(c.fileList);
            })
            .catch(e => {
                console.error('Request Attachment Error', e);
            })


    }


    /**
     * 
     */
    private readonly handleAttachmentRemove = (file: FileInfo, form: FormGroup, c: UploadComponent) => {
        const _path: string = this.getObjectKey(form.value) + '/';
        const _parent: any = form.value.ParentEditDataItem;
        const _approvalRemovePath = `/RequestAttachment?requestAttachmentIds=${file.uid}&RequestId=${_parent.RequestId}`;

        this.requestService.remove(_approvalRemovePath)
            .then(x => {
                this.s3Service.handleS3Remove(file, _path);
                const _files: any = form.get('Attachments').value;
                if (_files.length) {
                    const _index = _files.indexOf(_files.find(f => f.uid === file.uid));
                    if (_index >= 0) {
                        _files.splice(_index, 1);
                        form.get('Attachments').setValue(_files);
                    }
                } else {
                    _files.remove(file.uid);
                }
            })

    }


    /**
     * 
     */
    private readonly handleAttachmentDownload = (file: FileInfo, form: FormGroup) => {
        const _path: string = this.getObjectKey(form.value) + '/';
        this.s3Service.downloadS3Object(file, _path);
    }


    /**
     * 
     */
    private readonly isDelegateDisabled = (form, isPTE: boolean): boolean => {
        return isPTE ? !form.value.IsPTEDelegationEnabled :
            !form.value.IsDESDelegationEnabled;
    }

    /**
     * 
     */
    private readonly isUploadDisabled = (form): boolean => {
        return !form.value.EnableAttachments;
    }


    controls = [
        // Component Controls
        {
            type: 'action',
            name: 'AssignPLBtn',
            text: 'Assign Designer',
            title: 'Assign a Designer for this Work Approval',
            iconClass: 'fa fa-fw fa-user-plus',
            primary: true,
            click: this.handleAssignDesigner,
            stretch: true,
        },
        {
            type: 'upload',
            name: 'Attachments',
            title: 'Attachments',
            autoUpload: false,
            remove: this.handleAttachmentRemove,
            download: this.handleAttachmentDownload,
            save: this.handleAttachmentUpload,
            comment: true,
            stretch: true,
            disabled: this.isUploadDisabled
        },

        // Item Controls
        {
            type: 'label',
            name: 'ItemName',
            title: 'Item Description',
        },
        {
            type: 'label',
            name: 'Status',
            title: 'Status',
        },
        {
            type: 'user',
            name: 'AssignedDESName',
            title: 'Designer',
            emailField: 'AssignedDESEmail',
            delegate: this.handleDelegateUser,
            disabled: (form) => this.isDelegateDisabled(form, false)
        },
        {
            type: 'label',
            name: 'DESCompletionDate',
            title: 'Completion Date',
        },
        {
            type: 'action',
            title: 'Delete',
            text: 'Delete',
            name: 'desDeleteBtn',
            class: 'cgp-red',
            iconClass: 'fa fa-fw fa-trash-o',
            click: this.handleDelete,
        },
        {
            type: 'button-group',
            name: 'desCompleteBtn',
            buttons: [
                {
                    title: 'Cancel',
                    text: 'Cancel',
                    name: 'desCancelBtn',
                    iconClass: 'fa fa-fw fa-ban',
                    click: this.handleCancel,
                },
                {
                    title: 'Complete',
                    text: 'Complete',
                    name: 'desCompleteBtn',
                    iconClass: 'fa fa-fw fa-check',
                    primary: true,
                    click: this.handleComplete,
                },
            ]
        },
        {
            type: 'label',
            name: 'ECONumber',
            title: 'ECO#',
        },
        {
            type: 'label',
            name: 'IsLibPartImapcted',
            title: 'Library Impact?'
        },
        {
            type: 'user',
            name: 'AssignedPTEName',
            title: 'PTE Approver',
            emailField: 'AssignedPTEEmail',
            delegate: this.handleDelegateUser,
            disabled: (form) => this.isDelegateDisabled(form, true)
        },
        {
            type: 'label',
            name: 'PTECompletionDate',
            title: 'Review Date',
        },
        {
            type: 'button-group',
            name: 'pteApproveBtn',
            class: 'align-right',
            buttons: [
                {
                    title: 'Rework Request',
                    text: 'Rework Request',
                    name: 'pteReworkBtn',
                    iconClass: 'fa fa-fw fa-undo',
                    click: this.handleReject,
                },
                {
                    title: 'Approve',
                    text: 'Approve',
                    name: 'pteApproveBtn',
                    iconClass: 'fa fa-fw fa-check',
                    primary: true,
                    click: this.handleApprove,
                },
            ]
        },
    ]

}

const ComponentEditForm = {
    ComponentId: [],
    AssignPLBtn: [],
    ParentEditDataItem: [],
}

const ItemEditForm = {
    RequestItemId: [],
    DESWFTaskId: [],
    PTEWFTaskId: [],
    ItemId: [],
    ItemName: [],
    IsDESDelegationEnabled: [],
    IsPTEDelegationEnabled: [],

    Status: [],
    RejectComment: [],
    DESCompletionDate: [],
    PTECompletionDate: [],
    EnableDesignerAssignment: [],
    IsCancelEnabled: [],
    IsDeleteEnabled: [],

    AssignedDESName: [],
    AssignedDESEmail: [],
    AssignedPTEName: [],
    AssignedPTEEmail: [],
    ECONumber: [],
    IsLibPartImapcted: [],
    IsDESEnabled: [],
    IsPTEEnabled: [],

    pteReworkBtn: [],
    pteApproveBtn: [],
    ParentEditDataItem: [],

}
